import HttpFactory from "~/repository/factory";
import { createPublicRequestConfig } from "~/repository/authSession";
import type {
  PublicSchedulingBookingInput,
  PublicSchedulingEventType,
  SchedulingAvailability,
  SchedulingBooking,
  SchedulingEventType,
  SchedulingEventTypeInput,
  SchedulingHost,
  SchedulingMonthAvailability,
  SchedulingPage,
  SchedulingSlotsResponse,
} from "~/types/scheduling";

const publicRoot = "/api/scheduling/public/";
const anonymous = createPublicRequestConfig();

export class PublicSchedulingModule extends HttpFactory {
  getEventType(slug: string) {
    return this.get<PublicSchedulingEventType>(
      `${publicRoot}types/${encodeURIComponent(slug)}/`,
      {},
      anonymous,
    );
  }

  listSlots(slug: string, date: string, timezone: string) {
    return this.get<SchedulingSlotsResponse>(
      `${publicRoot}types/${encodeURIComponent(slug)}/slots/`,
      { date, timezone },
      anonymous,
    );
  }

  listMonthAvailability(slug: string, month: string) {
    return this.get<SchedulingMonthAvailability>(
      `${publicRoot}types/${encodeURIComponent(slug)}/availability/`,
      { month },
      anonymous,
    );
  }

  createBooking(slug: string, input: PublicSchedulingBookingInput) {
    return this.post<SchedulingBooking>(
      `${publicRoot}types/${encodeURIComponent(slug)}/bookings/`,
      input,
      anonymous,
    );
  }

  getBooking(token: string) {
    return this.get<SchedulingBooking>(
      `${publicRoot}bookings/${encodeURIComponent(token)}/`,
      {},
      anonymous,
    );
  }

  listBookingSlots(token: string, date: string, timezone: string) {
    return this.get<SchedulingSlotsResponse>(
      `${publicRoot}bookings/${encodeURIComponent(token)}/slots/`,
      { date, timezone },
      anonymous,
    );
  }

  listBookingMonthAvailability(token: string, month: string) {
    return this.get<SchedulingMonthAvailability>(
      `${publicRoot}bookings/${encodeURIComponent(token)}/availability/`,
      { month },
      anonymous,
    );
  }

  cancelBooking(token: string, clientRequestId: string) {
    return this.post<SchedulingBooking>(
      `${publicRoot}bookings/${encodeURIComponent(token)}/cancel/`,
      { client_request_id: clientRequestId },
      anonymous,
    );
  }

  rescheduleBooking(token: string, startsAt: string, clientRequestId: string) {
    return this.post<SchedulingBooking>(
      `${publicRoot}bookings/${encodeURIComponent(token)}/reschedule/`,
      { starts_at: startsAt, client_request_id: clientRequestId },
      anonymous,
    );
  }
}

export class SchedulingManagementModule extends HttpFactory {
  constructor(
    axiosInstance: ConstructorParameters<typeof HttpFactory>[0],
    private readonly root: string,
  ) {
    super(
      axiosInstance,
      root.startsWith("/api/organizations/") ? "school" : undefined,
    );
  }

  listEventTypes() {
    return this.get<SchedulingEventType[]>(`${this.root}event-types/`);
  }

  createEventType(input: SchedulingEventTypeInput) {
    return this.post<SchedulingEventType>(`${this.root}event-types/`, input);
  }

  updateEventType(id: number, input: Partial<SchedulingEventTypeInput>) {
    return this.patch<SchedulingEventType>(
      `${this.root}event-types/${id}/`,
      input,
    );
  }

  listHosts() {
    return this.get<SchedulingHost[]>(`${this.root}hosts/`);
  }

  getAvailability(hostId: number) {
    return this.get<SchedulingAvailability>(
      `${this.root}availability/${hostId}/`,
    );
  }

  setAvailability(
    hostId: number,
    input: Omit<SchedulingAvailability, "host_id">,
  ) {
    return this.put<SchedulingAvailability>(
      `${this.root}availability/${hostId}/`,
      input,
    );
  }

  listBookings(
    page = 1,
    filters: {
      starts_at__gte?: string;
      starts_at__lte?: string;
      page_size?: number;
    } = {},
  ) {
    return this.get<SchedulingPage<SchedulingBooking>>(
      `${this.root}bookings/`,
      { page, ...filters },
    );
  }

  updateBookingStatus(
    id: string,
    status: "completed" | "no_show" | "cancelled",
    version: number,
  ) {
    return this.post<SchedulingBooking>(`${this.root}bookings/${id}/status/`, {
      status,
      version,
    });
  }

  reassignBooking(id: string, hostId: number, version: number) {
    return this.post<SchedulingBooking>(
      `${this.root}bookings/${id}/reassign/`,
      {
        host_id: hostId,
        version,
      },
    );
  }

  associateProspect(id: string, prospectId: number | null) {
    return this.post<SchedulingBooking>(
      `${this.root}bookings/${id}/prospect/`,
      {
        prospect_id: prospectId,
      },
    );
  }
}
